// State machine driving the UI-overlay inversion-region editor on
// top of a UiInversionRenderer. Mirrors GridEditor in shape:
//
//   idle           → no editor active
//   drag-start     → toggle just turned on with no regions, OR user
//                    clicked "Add region." Waiting for first corner
//   drag-end       → first corner placed, rubber-banding to second
//   adjust         → at least one region exists. Active region shows
//                    handles. Click empty space inside the mat to
//                    deselect; click another region to switch active;
//                    drag region body to move; drag handle to resize.
//
// The editor doesn't own persisted state. `getInitialRegions()` reads
// the playmat theme's saved regions on start(); `onChange` fires on
// every mutation so the host (GameBoard) can pipe the in-flight array
// into tableScene.setUiInvertRegions() for a live shader preview.

const MIN_REGION_SIZE = 0.02;

function clamp01(v) { return Math.max(0, Math.min(1, v)); }

function rectFromUvDrag(start, end) {
  const x1 = Math.min(start.u, end.u);
  const y1 = Math.min(start.v, end.v);
  const x2 = Math.max(start.u, end.u);
  const y2 = Math.max(start.v, end.v);
  return {
    x: clamp01(x1),
    y: clamp01(y1),
    width: clamp01(x2 - x1),
    height: clamp01(y2 - y1),
  };
}

function isInsideRegion(uv, r) {
  return uv.u >= r.x && uv.u <= r.x + r.width
    && uv.v >= r.y && uv.v <= r.y + r.height;
}

export class UiInversionEditor {
  constructor({ renderer, getInitialRegions, onChange }) {
    this.renderer = renderer;
    this.getInitialRegions = getInitialRegions || (() => []);
    this.onChange = onChange || (() => {});
    this.mode = null;
    this.regions = [];
    this.activeIndex = -1;
    this.dragStart = null;
    this.dragEnd = null;
    this.draggingHandle = null;
    this.draggingRegionIndex = -1;
    this.dragOffset = null;
  }

  isActive() { return this.mode !== null; }

  start() {
    const initial = this.getInitialRegions() || [];
    this.regions = initial.map((r) => ({ ...r }));
    this.activeIndex = this.regions.length > 0 ? 0 : -1;
    this.mode = this.regions.length > 0 ? 'adjust' : 'drag-start';
    this.renderer.update(this.regions, this.activeIndex);
    this.onChange(this.regions);
  }

  stop() {
    this.mode = null;
    this.regions = [];
    this.activeIndex = -1;
    this.dragStart = null;
    this.dragEnd = null;
    this.draggingHandle = null;
    this.draggingRegionIndex = -1;
    this.dragOffset = null;
    this.renderer.clear();
  }

  getRegions() { return this.regions.map((r) => ({ ...r })); }

  addRegion() {
    this.mode = 'drag-start';
    this.dragStart = null;
    this.dragEnd = null;
  }

  deleteActive() {
    if (this.activeIndex < 0 || this.activeIndex >= this.regions.length) return;
    this.regions = this.regions.filter((_, i) => i !== this.activeIndex);
    this.activeIndex = this.regions.length > 0 ? 0 : -1;
    if (this.regions.length === 0) this.mode = 'drag-start';
    this.renderer.update(this.regions, this.activeIndex);
    this.onChange(this.regions);
  }

  pointerDown(uvPoint, handleMesh) {
    if (!uvPoint) return false;
    if (this.mode === 'drag-start') {
      this.dragStart = { ...uvPoint };
      this.dragEnd = { ...uvPoint };
      this.mode = 'drag-end';
      this.renderer.previewRect(this.dragStart, this.dragEnd);
      return true;
    }
    if (this.mode !== 'adjust') return false;
    // Handle drag (resize) wins over body drag. Even if the cursor
    // is also technically inside the region.
    if (handleMesh) {
      this.draggingHandle = handleMesh.userData;
      this.activeIndex = handleMesh.userData.regionIndex;
      this.renderer.update(this.regions, this.activeIndex);
      return true;
    }
    // Body drag (move). Search top-most region first so overlapping
    // regions can be picked individually.
    for (let i = this.regions.length - 1; i >= 0; i--) {
      if (isInsideRegion(uvPoint, this.regions[i])) {
        this.activeIndex = i;
        this.draggingRegionIndex = i;
        this.dragOffset = {
          du: uvPoint.u - this.regions[i].x,
          dv: uvPoint.v - this.regions[i].y,
        };
        this.renderer.update(this.regions, this.activeIndex);
        return true;
      }
    }
    // Click outside any region. Deselect.
    this.activeIndex = -1;
    this.draggingRegionIndex = -1;
    this.draggingHandle = null;
    this.renderer.update(this.regions, this.activeIndex);
    return true;
  }

  pointerMove(uvPoint) {
    if (!uvPoint) return false;
    if (this.mode === 'drag-end') {
      this.dragEnd = { ...uvPoint };
      this.renderer.previewRect(this.dragStart, this.dragEnd);
      return true;
    }
    if (this.mode !== 'adjust') return false;
    if (this.draggingHandle) {
      this.resizeViaHandle(uvPoint);
      this.renderer.update(this.regions, this.activeIndex);
      this.onChange(this.regions);
      return true;
    }
    if (this.draggingRegionIndex >= 0) {
      const r = this.regions[this.draggingRegionIndex];
      const newX = clamp01(uvPoint.u - this.dragOffset.du);
      const newY = clamp01(uvPoint.v - this.dragOffset.dv);
      this.regions[this.draggingRegionIndex] = {
        ...r,
        x: Math.min(newX, 1 - r.width),
        y: Math.min(newY, 1 - r.height),
      };
      this.renderer.update(this.regions, this.activeIndex);
      this.onChange(this.regions);
      return true;
    }
    return false;
  }

  pointerUp() {
    if (this.mode === 'drag-end') {
      const rect = rectFromUvDrag(this.dragStart, this.dragEnd);
      this.dragStart = null;
      this.dragEnd = null;
      if (rect.width >= MIN_REGION_SIZE && rect.height >= MIN_REGION_SIZE) {
        this.regions = [...this.regions, rect];
        this.activeIndex = this.regions.length - 1;
        this.mode = 'adjust';
        this.renderer.update(this.regions, this.activeIndex);
        this.onChange(this.regions);
      } else {
        // Drag too small. Stay in drag-start so the user can retry.
        this.renderer.update(this.regions, this.activeIndex);
      }
      return true;
    }
    if (this.draggingHandle || this.draggingRegionIndex >= 0) {
      this.draggingHandle = null;
      this.draggingRegionIndex = -1;
      this.dragOffset = null;
      return true;
    }
    return false;
  }

  resizeViaHandle(uvPoint) {
    const { regionIndex, corner } = this.draggingHandle;
    const r = this.regions[regionIndex];
    if (!r) return;
    let x1 = r.x;
    let y1 = r.y;
    let x2 = r.x + r.width;
    let y2 = r.y + r.height;
    const u = clamp01(uvPoint.u);
    const v = clamp01(uvPoint.v);
    if (corner.includes('w')) x1 = u;
    if (corner.includes('e')) x2 = u;
    if (corner.includes('n')) y1 = v;
    if (corner.includes('s')) y2 = v;
    if (x2 < x1) [x1, x2] = [x2, x1];
    if (y2 < y1) [y1, y2] = [y2, y1];
    this.regions[regionIndex] = {
      x: x1,
      y: y1,
      width: Math.max(MIN_REGION_SIZE, x2 - x1),
      height: Math.max(MIN_REGION_SIZE, y2 - y1),
    };
  }
}
